import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, ImagePlus, Sparkles, Sigma, Timer, Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TID } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

// ── Machine area options (heads × frame-mm) — Surat multi-head machines ────
export const MACHINE_AREAS = [
  { v: "1x0",    heads: 1,  frame: 0,   label: "Single head" },
  { v: "12x250", heads: 12, frame: 250, label: "12 × 250mm" },
  { v: "15x250", heads: 15, frame: 250, label: "15 × 250mm" },
  { v: "20x250", heads: 20, frame: 250, label: "20 × 250mm" },
  { v: "24x250", heads: 24, frame: 250, label: "24 × 250mm" },
  { v: "12x300", heads: 12, frame: 300, label: "12 × 300mm" },
  { v: "15x300", heads: 15, frame: 300, label: "15 × 300mm" },
  { v: "21x300", heads: 21, frame: 300, label: "21 × 300mm" },
  { v: "24x300", heads: 24, frame: 300, label: "24 × 300mm" },
  { v: "12x400", heads: 12, frame: 400, label: "12 × 400mm" },
  { v: "15x400", heads: 15, frame: 400, label: "15 × 400mm" },
  { v: "20x400", heads: 20, frame: 400, label: "20 × 400mm" },
  { v: "21x400", heads: 21, frame: 400, label: "21 × 400mm" },
  { v: "24x400", heads: 24, frame: 400, label: "24 × 400mm" },
  { v: "27x400", heads: 27, frame: 400, label: "27 × 400mm" },
  { v: "32x400", heads: 32, frame: 400, label: "32 × 400mm" },
  { v: "15x450", heads: 15, frame: 450, label: "15 × 450mm" },
  { v: "20x450", heads: 20, frame: 450, label: "20 × 450mm" },
  { v: "12x500", heads: 12, frame: 500, label: "12 × 500mm" },
  { v: "15x500", heads: 15, frame: 500, label: "15 × 500mm" },
  { v: "20x500", heads: 20, frame: 500, label: "20 × 500mm" },
  { v: "24x500", heads: 24, frame: 500, label: "24 × 500mm" },
  { v: "12x600", heads: 12, frame: 600, label: "12 × 600mm" },
  { v: "15x600", heads: 15, frame: 600, label: "15 × 600mm" },
  { v: "20x600", heads: 20, frame: 600, label: "20 × 600mm" },
  { v: "24x600", heads: 24, frame: 600, label: "24 × 600mm" },
  { v: "12x750", heads: 12, frame: 750, label: "12 × 750mm" },
  { v: "15x750", heads: 15, frame: 750, label: "15 × 750mm" },
];

// ── Surat embroidery industry options ──────────────────────────────────────
const GARMENTS = [
  { v: "Blouse",          k: "blouse" },
  { v: "Kurti",           k: "kurti" },
  { v: "Saree border",    k: "sareeBorder" },
  { v: "Lehenga",         k: "lehenga" },
  { v: "Dupatta",         k: "dupatta" },
  { v: "Salwar",          k: "salwar" },
  { v: "Anarkali",        k: "anarkali" },
  { v: "Gown",            k: "gown" },
  { v: "Odhni / Chunri",  k: "odhni" },
  { v: "Ghagra / Skirt",  k: "ghagra" },
  { v: "Jacket / Shrug",  k: "jacket" },
  { v: "Waistcoat / Koti",k: "waistcoat" },
  { v: "Saree pallu",     k: "sareePallu" },
  { v: "Kurta (gents)",   k: "kurta" },
];

const BRANDS = [
  "Tajima", "Barudan", "ZSK", "Sunstar", "Happy", "Brother",
  "Feiya", "Richpeace", "SWF", "Butterfly", "Juki",
];

const FABRICS = [
  "Cotton", "Silk", "Georgette", "Chiffon", "Net", "Velvet", "Rayon",
  "Organza", "Art Silk / Nylon", "Dupion Silk", "Crepe", "Mono-net",
  "Brasso", "Linen", "Banarasi", "Satin", "Tussar",
];

const OUTPUT_FORMATS = [
  { v: "DST", label: "DST (Tajima / most machines)" },
  { v: "PES", label: "PES (Brother)" },
  { v: "EXP", label: "EXP (Melco / Elna)" },
  { v: "JEF", label: "JEF (Janome)" },
  { v: "VP3", label: "VP3 (Husqvarna / Viking)" },
];

// ── Embellishments (bits) config ────────────────────────────────────────────
// has_count: true → show piece-count input; grams always shown
const BITS_TYPES = [
  { key: "zari",      labelKey: "zari",      has_count: false },
  { key: "sequin",    labelKey: "sequin",    has_count: true  },
  { key: "moti",      labelKey: "moti",      has_count: true  },
  { key: "mirror",    labelKey: "mirror",    has_count: true  },
  { key: "kundan",    labelKey: "kundan",    has_count: true  },
  { key: "stone",     labelKey: "stone",     has_count: true  },
  { key: "khatli",    labelKey: "khatli",    has_count: false },
  { key: "gota",      labelKey: "gota",      has_count: false },
  { key: "pipe_bead", labelKey: "pipe_bead", has_count: true  },
  { key: "nakh",      labelKey: "nakh",      has_count: true  },
];

function emptyBit() { return { count: 0, grams: 0 }; }
function defaultBitsDetail() {
  return Object.fromEntries(BITS_TYPES.map((b) => [b.key, emptyBit()]));
}

const STAGES = ["procAnalyzing", "procDetecting", "procCalculating", "procGenerating"];

export default function NewDesign() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [form, setForm] = useState({
    design_name: "",
    garment_type: "Blouse",
    machine_brand: "Tajima",
    fabric_type: "Cotton",
    machine_area: "12x400",
    stitch_count: 12000,
    machine_time_minutes: 45,
    thread_colors: 4,
    has_zari: false,
    has_sequin: false,
    has_moti: false,
    has_mirror: false,
    has_kundan: false,
    has_stone: false,
    has_khatli: false,
    has_gota: false,
    has_pipe_bead: false,
    has_nakh: false,
    output_format: "DST",
  });

  // Load user's default machine area from cost settings
  useEffect(() => {
    api.get("/cost-settings").then(({ data }) => {
      if (data.default_machine_area) {
        setForm((p) => ({ ...p, machine_area: data.default_machine_area }));
      }
    }).catch(() => {});
  }, []);
  const [bitsDetail, setBitsDetail] = useState(defaultBitsDetail());
  const [stage, setStage] = useState(-1);
  const [busy, setBusy] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);

  const onPick = (f) => {
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setAnalysis(null);
  };

  const analyze = async () => {
    if (!file) return;
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      fd.append("lang", lang);
      const { data } = await api.post("/analyze-design", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setAnalysis(data);

      // Fill main form fields
      setForm((p) => ({
        ...p,
        stitch_count: data.stitch_count,
        machine_time_minutes: data.machine_time_minutes,
        thread_colors: data.thread_colors,
        has_zari: !!data.has_zari,
        has_sequin: !!data.has_sequin,
        has_moti: !!data.has_moti,
        has_mirror: !!data.has_mirror,
        has_kundan: !!data.has_kundan,
        has_stone: !!data.has_stone,
        has_khatli: !!data.has_khatli,
        has_gota: !!data.has_gota,
        has_pipe_bead: !!data.has_pipe_bead,
        has_nakh: !!data.has_nakh,
      }));

      // Fill bits detail quantities from AI
      if (Array.isArray(data.bits_details) && data.bits_details.length > 0) {
        const newBits = defaultBitsDetail();
        data.bits_details.forEach((b) => {
          if (newBits[b.type] !== undefined) {
            newBits[b.type] = {
              count: b.approx_count || 0,
              grams: b.approx_grams || 0,
              variant: b.variant || "",
            };
          }
        });
        setBitsDetail(newBits);
      }

      toast.success(t("aiFilled"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI unavailable");
    } finally { setAnalyzing(false); }
  };

  const submit = async () => {
    if (!file) { toast.error(t("uploadTitle")); return; }
    if (form.stitch_count <= 0 || form.machine_time_minutes <= 0) {
      toast.error(t("stitches")); return;
    }
    setBusy(true);
    for (let i = 0; i < STAGES.length; i++) {
      setStage(i);
      await new Promise((r) => setTimeout(r, 650));
    }
    try {
      const fd = new FormData();
      fd.append("image", file);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));

      // Build bits_detail JSON — only include enabled types
      const activeBits = {};
      BITS_TYPES.forEach(({ key }) => {
        if (form[`has_${key}`]) activeBits[key] = bitsDetail[key];
      });
      fd.append("bits_detail", JSON.stringify(activeBits));

      const { data } = await api.post("/designs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      nav(`/results/${data.design.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setStage(-1);
    } finally { setBusy(false); }
  };

  const setBit = (key, field, val) =>
    setBitsDetail((p) => ({ ...p, [key]: { ...p[key], [field]: val } }));

  if (stage >= 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center">
        <div className="relative w-24 h-24 mb-6">
          <div className="absolute inset-0 rounded-full border-4 border-primary/20" />
          <div className="absolute inset-0 rounded-full border-4 border-primary border-t-transparent animate-spin-slow" />
          <Sparkles size={26} className="absolute inset-0 m-auto text-primary" />
        </div>
        <div className="sq-label mb-2">{t("uploadTitle")}</div>
        <h2 data-testid={TID.processingStage} className="text-2xl font-heading font-bold">{t(STAGES[stage])}</h2>
        <div className="mt-6 flex gap-2">
          {STAGES.map((s, i) => (
            <span key={s} className={`h-1.5 w-8 rounded-full ${i <= stage ? "bg-primary" : "bg-muted"}`} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="sq-label">{t("uploadTitle")}</div>
        <h1 className="text-3xl font-heading font-bold mt-1 text-secondary">{t("newDesign")}</h1>
      </div>

      {/* Upload */}
      <div className="sq-card p-4">
        {preview ? (
          <div className="space-y-3">
            <img src={preview} data-testid={TID.imagePreview} alt="preview"
              className="w-full max-h-72 object-contain rounded-lg bg-muted" />
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => galleryRef.current?.click()} className="h-12">
                {t("changePhoto")}
              </Button>
              <Button
                data-testid={TID.analyzeBtn}
                onClick={analyze}
                disabled={analyzing}
                className="h-12 bg-secondary text-secondary-foreground hover:bg-secondary/90"
              >
                {analyzing ? <Loader2 className="animate-spin mr-2" size={16} /> : <Wand2 size={16} className="mr-2" />}
                {t("aiAnalyze")}
              </Button>
            </div>

            {analysis && (
              <div className="rounded-lg bg-accent/15 border border-accent/40 p-4 text-sm space-y-3"
                data-testid={TID.aiResult}>
                <div className="flex items-center justify-between">
                  <div className="sq-label text-accent-foreground/80">{t("aiEstimate")}</div>
                  <span className="px-2 py-0.5 rounded-full bg-surface border border-border text-[10px] uppercase tracking-widest">
                    {analysis.complexity}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <AiStat label={t("stitches")} value={Number(analysis.stitch_count).toLocaleString("en-IN")} />
                  <AiStat label={t("machineTime")} value={`${analysis.machine_time_minutes} ${t("minutes")}`} />
                  <AiStat label={t("threadColors")} value={analysis.thread_colors} />
                </div>

                {analysis.cost_estimate && (
                  <div className="rounded-lg bg-surface border border-border p-3 flex items-baseline justify-between">
                    <span className="sq-label">{t("approxPricePerPiece")}</span>
                    <span className="sq-stat text-2xl text-primary" data-testid="ai-price-per-piece">
                      ₹{Math.round(analysis.cost_estimate.price_per_piece).toLocaleString("en-IN")}
                    </span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <MetaRow label={t("aiCoverage")} value={`${analysis.coverage_area_sqcm} cm²`} />
                  <MetaRow label={t("aiSize")}
                    value={`${analysis.design_size_cm?.width}×${analysis.design_size_cm?.height} cm`} />
                  <MetaRow label={t("aiFillType")} value={analysis.fill_type} />
                </div>

                {/* Bits details from AI */}
                {Array.isArray(analysis.bits_details) && analysis.bits_details.length > 0 && (
                  <div>
                    <div className="sq-label mb-1.5">{t("aiBitsDetected")}</div>
                    <div className="space-y-1">
                      {analysis.bits_details.map((b, i) => (
                        <div key={i} className="flex items-start justify-between text-xs py-1 border-b border-dashed border-border last:border-0">
                          <div>
                            <span className="font-semibold capitalize">{t(b.type) || b.type}</span>
                            {b.variant && <span className="text-muted-foreground ml-1">({b.variant})</span>}
                          </div>
                          <div className="text-right text-muted-foreground">
                            {b.approx_count ? <span>{b.approx_count.toLocaleString("en-IN")} pcs · </span> : null}
                            {b.approx_grams ? <span>~{b.approx_grams} g</span> : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(analysis.motifs || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {analysis.motifs.map((m) => (
                      <span key={m} className="px-2 py-0.5 rounded-full bg-surface border border-border text-xs">{m}</span>
                    ))}
                  </div>
                )}
                {analysis.notes && (
                  <div>
                    <div className="sq-label mb-0.5">{t("aiNotes")}</div>
                    <div className="text-foreground">{analysis.notes}</div>
                  </div>
                )}
                {analysis.recommendation && (
                  <div>
                    <div className="sq-label mb-0.5">{t("aiTip")}</div>
                    <div className="text-foreground">{analysis.recommendation}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => cameraRef.current?.click()} data-testid={TID.captureBtn}
              className="h-32 rounded-xl border-2 border-dashed border-border hover:border-primary/60 flex flex-col items-center justify-center gap-2 sq-tap">
              <Camera size={24} className="text-primary" />
              <span className="font-semibold">{t("tapCamera")}</span>
            </button>
            <button onClick={() => galleryRef.current?.click()} data-testid={TID.galleryBtn}
              className="h-32 rounded-xl border-2 border-dashed border-border hover:border-primary/60 flex flex-col items-center justify-center gap-2 sq-tap">
              <ImagePlus size={24} className="text-primary" />
              <span className="font-semibold">{t("tapGallery")}</span>
            </button>
          </div>
        )}
        <input ref={cameraRef} data-testid={TID.fileInput} type="file" accept="image/*" capture="environment"
          className="hidden" onChange={(e) => onPick(e.target.files?.[0])} />
        <input ref={galleryRef} type="file" accept="image/*"
          className="hidden" onChange={(e) => onPick(e.target.files?.[0])} />
      </div>

      {/* Design details */}
      <div className="sq-card p-4 space-y-4">
        <h2 className="font-heading font-semibold text-lg">{t("details")}</h2>

        <Field label={t("designName")}>
          <Input data-testid={TID.designNameInput} className="h-12" value={form.design_name}
            onChange={(e) => setForm({ ...form, design_name: e.target.value })} placeholder="Rose Butta v1" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t("garmentType")}>
            <Select value={form.garment_type} onValueChange={(v) => setForm({ ...form, garment_type: v })}>
              <SelectTrigger className="h-12" data-testid={TID.garmentSelect}><SelectValue /></SelectTrigger>
              <SelectContent>
                {GARMENTS.map((g) => <SelectItem key={g.v} value={g.v}>{t(g.k)}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("machineBrand")}>
            <Select value={form.machine_brand} onValueChange={(v) => setForm({ ...form, machine_brand: v })}>
              <SelectTrigger className="h-12" data-testid={TID.brandSelect}><SelectValue /></SelectTrigger>
              <SelectContent>{BRANDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>

        <Field label={t("fabricType")}>
          <Select value={form.fabric_type} onValueChange={(v) => setForm({ ...form, fabric_type: v })}>
            <SelectTrigger className="h-12" data-testid={TID.fabricInput}><SelectValue /></SelectTrigger>
            <SelectContent>{FABRICS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
          </Select>
        </Field>

        <Field label={t("machineArea")}>
          <Select value={form.machine_area} onValueChange={(v) => setForm({ ...form, machine_area: v })}>
            <SelectTrigger className="h-12"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MACHINE_AREAS.map((a) => (
                <SelectItem key={a.v} value={a.v}>
                  {a.label}{a.heads > 1 ? ` — ${a.heads} ${t("heads")}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(() => {
            const info = MACHINE_AREAS.find((a) => a.v === form.machine_area);
            return info && info.frame > 0 ? (
              <p className="text-xs text-muted-foreground mt-1">
                {t("headsPerMeterHint").replace("{hpm}", (1000 / info.frame).toFixed(1)).replace("{mm}", info.frame)}
              </p>
            ) : null;
          })()}
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t("stitches")} icon={<Sigma size={14} />}>
            <Input data-testid={TID.stitchCountInput} type="number" inputMode="numeric" className="h-12"
              value={form.stitch_count}
              onChange={(e) => setForm({ ...form, stitch_count: parseInt(e.target.value || "0", 10) })} />
          </Field>
          <Field label={t("machineTime")} icon={<Timer size={14} />}>
            <Input data-testid={TID.machineTimeInput} type="number" inputMode="numeric" className="h-12"
              value={form.machine_time_minutes}
              onChange={(e) => setForm({ ...form, machine_time_minutes: parseInt(e.target.value || "0", 10) })} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t("threadColors")}>
            <Input data-testid={TID.colorsInput} type="number" inputMode="numeric" className="h-12" min={1} max={12}
              value={form.thread_colors}
              onChange={(e) => setForm({ ...form, thread_colors: Math.max(1, parseInt(e.target.value || "1", 10)) })} />
          </Field>
          <Field label={t("outputFormat")}>
            <Select value={form.output_format} onValueChange={(v) => setForm({ ...form, output_format: v })}>
              <SelectTrigger className="h-12" data-testid={TID.formatSelect}><SelectValue /></SelectTrigger>
              <SelectContent>
                {OUTPUT_FORMATS.map((f) => <SelectItem key={f.v} value={f.v}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
      </div>

      {/* Embellishments (bits) */}
      <div className="sq-card p-4 space-y-3">
        <div>
          <h2 className="font-heading font-semibold text-lg">{t("embellishments")}</h2>
          <p className="text-xs text-muted-foreground mt-0.5">{t("embellishmentsHint")}</p>
        </div>

        {BITS_TYPES.map(({ key, labelKey, has_count }) => {
          const isOn = !!form[`has_${key}`];
          return (
            <div key={key} className="rounded-lg border border-border bg-surface overflow-hidden">
              <label className="flex items-center justify-between h-12 px-4 cursor-pointer">
                <span className="font-medium">{t(labelKey)}</span>
                <Switch
                  checked={isOn}
                  onCheckedChange={(v) => setForm({ ...form, [`has_${key}`]: v })}
                  data-testid={`bits-${key}`}
                />
              </label>
              {isOn && (
                <div className="border-t border-border bg-muted/30 px-4 py-2.5 grid grid-cols-2 gap-3">
                  {has_count && (
                    <div>
                      <div className="sq-label mb-1">{t("approxCount")}</div>
                      <Input
                        type="number" inputMode="numeric" className="h-9"
                        placeholder="0"
                        value={bitsDetail[key].count || ""}
                        onChange={(e) => setBit(key, "count", parseInt(e.target.value || "0", 10))}
                      />
                    </div>
                  )}
                  <div className={has_count ? "" : "col-span-2"}>
                    <div className="sq-label mb-1">{t("approxGrams")}</div>
                    <Input
                      type="number" inputMode="decimal" className="h-9"
                      placeholder="0.0"
                      value={bitsDetail[key].grams || ""}
                      onChange={(e) => setBit(key, "grams", parseFloat(e.target.value || "0"))}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Button data-testid={TID.generateBtn} onClick={submit} disabled={busy || !file}
        className="h-14 w-full text-lg font-semibold sq-tap">
        {busy ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
        {t("generate")}
      </Button>
    </div>
  );
}

function Field({ label, icon, children }) {
  return (
    <div>
      <Label className="sq-label flex items-center gap-1">{icon}{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function AiStat({ label, value }) {
  return (
    <div className="rounded-lg bg-surface border border-border p-2">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-heading font-bold text-base leading-tight mt-0.5">{value}</div>
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-dashed border-border pb-1">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold capitalize">{value}</span>
    </div>
  );
}
